import HelpScrollArea from 'components/common/help-scroll-area';
import React, { useState, useEffect } from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";

import HelpTabsSearchField from "../../../search-field";
import DeclarationsExamplesListItem from "../../../../../../../../../../../../../../../../common/declarationsExamplesListItem";
import {
  useLazyGetDeclarationsQuery,
} from "../../../../../../../../../../../../../../../../../api/dictionary";
import Preloader from "../../../../../../../../../../../../../../../../common/preloader";
import {COLORS, DECLARATION_EXAMPLES_PAGE_SIZE} from "../../../../../../../../../../../../../../../../../config/constants";

import './declarations_examples_list.scss'

type Props = {
  code?: string,
  handleApplyCode(code: string): void;
}

const DeclarationsExamplesList = ({handleApplyCode, code} : Props) => {

  const { t } = useTranslation()

  const [ examples, setExamples ] = useState<DeclarationsOutput[]>([]);
  const [ notFoundError, setNotFoundError ] = useState<boolean | string>(false);
  const [ isLoading, setIsLoading ] = useState<boolean | string>(false);
  const [ offset, setOffset ] = useState(0);
  const [ hasMore, setHasMore ] = useState(true);
  const [ currentCode, setCurrentCode ] = useState('');

  const [ declarationsRequest ] = useLazyGetDeclarationsQuery()

  const loadExamples = async (code: string, newOffset = 0) => {

    const codeWithoutSpaces = code.replace(/\s+/g, "");

    if (codeWithoutSpaces.length !== 10) return;

    setIsLoading(true);
    setNotFoundError(false);

    try {
      const resp = await declarationsRequest({
        code: codeWithoutSpaces,
        limit: DECLARATION_EXAMPLES_PAGE_SIZE,
        offset: newOffset
      }).unwrap();

      if (newOffset === 0) {
        setExamples(resp);
      } else {
        setExamples(previous => [ ...previous, ...resp ]);
      }

      if (!resp.length && newOffset === 0) {
        setExamples([]);
        setNotFoundError(true);
        return;
      }

      setHasMore(resp.length === DECLARATION_EXAMPLES_PAGE_SIZE);
      setOffset(newOffset);

    } catch (error) {
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = (code: string) => {
    if (isLoading || !hasMore) return;
    loadExamples(code, offset + DECLARATION_EXAMPLES_PAGE_SIZE);
  };

  const handleSearch = (code: string) => {
    if (!code) {
      setNotFoundError(false);
      return;
    }

    setCurrentCode(code);
    loadExamples(code);
  };

  useEffect(() => {
    if (code) {
      loadExamples(code);
      setCurrentCode(code);
    }
  }, [ code ])

  const hasExamples = Boolean(examples?.length);

  return <>
    <HelpTabsSearchField
      onSearch={handleSearch}
      label={t('calc__text__packaging__HSCodeOrKeywords')}
      code={code}
    />
    {notFoundError && <Typography variant='caption' sx={{ marginTop: 1, display: 'inline-block' }}>
      {typeof notFoundError === 'string' ? notFoundError : t('calc__text__main__noOptionsFound')}
    </Typography>}
    {!hasExamples && isLoading && (<div className='guide-preloader'>
      <Preloader/>
    </div>)}
    {hasExamples && (
      <HelpScrollArea persistentScrollbar className='declarations-examples-list declarations-examples-list_tabs' contentClassName='declarations-examples-list__content' label={t('calc__text__packaging__examplesOfDeclarations')}>
        {examples?.map((example, index) => (
          <DeclarationsExamplesListItem
            isHelpTab
            key={index}
            handleApplyCode={handleApplyCode}
            code={example.code}
            title={example.title}
          />
        ))}

        {hasMore && !isLoading && <Typography variant='caption'
          sx={{
            display: 'block',
            color: COLORS.BLUE,
            marginLeft: '104px',
            fontSize: '12px',
            lineHeight: '18px',
            letterSpacing: 0,
          }}
          onClick={() => loadMore(currentCode)}
          className='declarations-examples-list__showMore'>
          {t('calc__text__main__showMoreExamples')}
        </Typography>}
        {isLoading && <div className='declarations-examples-list__preloader'><Preloader/></div>}
      </HelpScrollArea>
    )}
  </>

}

export default DeclarationsExamplesList

